import { useMemo, useState, useEffect, useRef } from "react";
import { Formik } from "formik";
import { isEqual } from "lodash";
import { CloseOutlined } from "@ant-design/icons";
import { Select, notification, Input, Col, Row, DatePicker } from "antd";
import { PAYMENT_TYPE, PAYMENT_CATEGORY_PAYMENT_RECEIVED, PAYMENT_STATUS, dateFormat, DELAY } from "../../../../../../Common/constants";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { usePaymentReceivedReportUIContext } from "../PaymentReceivedReportUIContext";
import { optimzedCustomerSearch, optimzedSaleOrderRequest } from "../../../../../../Sale/_redux/orders/ordersCrud";
import {
  getSettingKey,
  getTimeByTimeZone,
  toAbsoluteUrl,
  handleNumericInput,
  displayValue,
  getDateTimeInZone,
} from "../../../../../../Common/utils";
import moment from "moment";
import SVG from "react-inlinesvg";
import AdvancedFilterPopover from "./../../../../../../Common/AdvancedFiltersPopover";
import { fetchUsers } from "./../../../../../../User/_redux/users/usersActions";
import { getAllChannels } from "./../../../../../../Inventory/_redux/sale-stock-channels/saleStockChannelsCrud";
import { DebounceInput } from "react-debounce-input";
import { defaultValuesFilterReport, withDefaultReportFilters } from "../../paymentReportFilterDefaults";

const { Option } = Select;
const { RangePicker } = DatePicker;

export const Custom_Status_Name = {
  [PAYMENT_STATUS.PENDING.value]: "Payment In Progress",
};

export function PaymentReceivedReportFilter({
  popoverVisible,
  setPopoverVisible,
  prepareFilter,
  startDate,
  endDate,
  setShowAppliedFilters,
}) {
  const localStorageFilter = () => localStorage.getItem("payment_receive_filter") || null;
  const formRef = useRef({});

  const [channelOptions, setChannelOptions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [saleOrders, setSaleOrders] = useState([]);
  const [minPayment, setMinPayment] = useState(0);
  const [maxPayment, setMaxPayment] = useState(null);
  const dispatch = useDispatch();
  const paymentReceivedReportUIContext = usePaymentReceivedReportUIContext();
  const paymentReceivedReportUIProps = useMemo(() => {
    return {
      setQueryParams: paymentReceivedReportUIContext.setQueryParams,
      queryParams: paymentReceivedReportUIContext.queryParams,
    };
  }, [paymentReceivedReportUIContext]);

  const { currentBasicState } = useSelector((state) => ({ currentBasicState: state.basicSettings }), shallowEqual);
  const timeZone = getSettingKey(currentBasicState.entity, "Basic", "timezone");
  const currencySymbol = getSettingKey(currentBasicState.entity, "Basic", "currency_symbol");
  const [displayAdvanceFilters, setDisplayAdvanceFilters] = useState(
    JSON.parse(localStorage.getItem("payment_receive_filter")) !== null &&
      JSON.parse(localStorage.getItem("payment_receive_filter")).display_advance_filters
      ? true
      : false
  );

  useEffect(() => {
    if (formRef.current && startDate && endDate) {
      formRef.current.setValues({
        ...formRef.current.values,
        start_date: moment(getTimeByTimeZone(timeZone, startDate)).format(dateFormat),
        end_date: moment(getTimeByTimeZone(timeZone, endDate)).format(dateFormat),
      });
    }
  }, [startDate, endDate, timeZone]);

  const applyFilter = (values) => {
    localStorage.setItem(
      "payment_receive_filter",
      JSON.stringify({
        advance_filters: {
          ...values,
          cheque_date: values?.cheque_date
            ? typeof values?.cheque_date == "string"
              ? values?.cheque_date
              : values?.cheque_date?.format("YYYY-MM-DD")
            : "",
        },
        display_advance_filters: displayAdvanceFilters,
        show_advance_filters: true,
      })
    );
    const newQueryParams = prepareFilter(paymentReceivedReportUIProps.queryParams, values, timeZone);

    if (!isEqual(newQueryParams, paymentReceivedReportUIProps.queryParams)) {
      newQueryParams.pageNumber = 1;
      newQueryParams.page = 1;
      paymentReceivedReportUIProps.setQueryParams(newQueryParams);
    }
  };

  const getCustomer = (params) => {
    optimzedCustomerSearch(params)
      .then((response) => {
        setCustomers(response.data.entities);
      })
      .catch((err) => {
        err.response.data.errors.map((error) => notification.error({ message: error }));
      });
  };

  const getChannels = (params) => {
    getAllChannels(params).then((response) => {
      setChannelOptions(response.data.entities);
    });
  };

  useEffect(() => {
    getCustomer({ search_key: "" });
    getSaleListings({});
    getChannels({ search_key: "" });

    try {
      const rawData = localStorageFilter();
      const payment_receive_filter = rawData ? JSON.parse(rawData) : {};
      const storedFilter = withDefaultReportFilters(payment_receive_filter.advance_filters || {});

      const updatedData = {
        ...payment_receive_filter,
        show_advance_filters: true,
        display_advance_filters: true,
        advance_filters: storedFilter,
      };

      localStorage.setItem("payment_receive_filter", JSON.stringify(updatedData));
      formRef.current?.setValues?.(storedFilter);
      setShowAppliedFilters?.(false);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const getSaleListings = async (params) => {
    try {
      const response = await optimzedSaleOrderRequest(params);
      setSaleOrders(response.data.entities);
    } catch (err) {
      err.response.data.errors.map((message) => notification.error({ message }));
    }
  };

  const initialValues = {
    brand_ids: [],
    customer_ids: [],
    payment_type: [],
    payment_categories: undefined,
    search_key: "",
    customer_balance_type: "",
    page: 1,
    page_size: 20,
    payment_status: [],
    reference_no: "",
    sale_order_no: "",
    cheque_date: "",
    user_ids: [],
    cheque_no: "",
    min_payment: 0,
    max_payment: null,
    channel_ids: [],
  };

  const { usersState } = useSelector(
    (state) => ({
      usersState: state.users,
    }),
    shallowEqual
  );
  const { entities: users } = usersState;

  useEffect(() => {
    getUsersList();
  }, []);

  const getUsersList = (searchKey = "") => {
    dispatch(
      fetchUsers({
        pageNumber: 1,
        pageSize: 20,
        searchKey,
      })
    );
  };

  const initialValuesWithDefault = () => {
    const initialFilter = { ...initialValues, ...defaultValuesFilterReport };
    localStorage.setItem(
      "payment_receive_filter",
      JSON.stringify({ advance_filters: initialFilter, show_advance_filters: true, display_advance_filters: true })
    );
    return initialFilter;
  };

  const getFormInitialValues = () => {
    try {
      const stored = JSON.parse(localStorage.getItem("payment_receive_filter"));
      if (stored?.advance_filters && typeof stored.advance_filters === "object") {
        return {
          ...initialValues,
          ...defaultValuesFilterReport,
          ...stored.advance_filters,
        };
      }
    } catch (e) {
      console.error(e);
    }

    return {
      ...initialValuesWithDefault(),
      start_date: startDate ? moment(getTimeByTimeZone(timeZone, startDate)).format(dateFormat) : "",
      end_date: endDate ? moment(getTimeByTimeZone(timeZone, endDate)).format(dateFormat) : "",
    };
  };

  const advanceFilterContent = (values, setFieldValue) => (
    <div className="product-advance-filter-form">
      <div className="product-advance-filter-field">
        <label>Filter by Payment Status</label>
        <Select
          dropdownClassName="po-report-select-dropdown"
          mode="multiple"
          maxTagCount={"responsive"}
          showSearch
          optionFilterProp="children"
          allowClear
          filterOption={true}
          value={values?.payment_status || []}
          showArrow
          placeholder="Select Payment Status"
          onChange={(value) => {
            setFieldValue("payment_status", value);
          }}
        >
          {Object.keys(PAYMENT_STATUS).map(
            (paymentEle) =>
              !["UNKNOWN", "RETURNED", "PARTIALLY_REFUNDED", "FULLY_REFUNDED"].includes(paymentEle) && (
                <Option value={PAYMENT_STATUS[paymentEle].value} key={PAYMENT_STATUS[paymentEle].value}>
                  {Custom_Status_Name[PAYMENT_STATUS[paymentEle].value] || PAYMENT_STATUS[paymentEle].name}
                </Option>
              )
          )}
        </Select>
      </div>
      <div className="product-advance-filter-field">
        <label>Filter by Business Name</label>
        <Select
          dropdownClassName="po-report-select-dropdown"
          mode="multiple"
          maxTagCount={"responsive"}
          showArrow
          value={values ? values?.customer_ids : []}
          showSearch
          optionFilterProp="children"
          filterOption={false}
          name="customer_ids"
          placeholder={"Select Customer"}
          onChange={(status) => {
            setFieldValue("customer_ids", status);
          }}
          onSearch={(search_key) => {
            getCustomer({ search_key });
          }}
        >
          {customers?.length !== 0
            ? customers.map((customer) => (
                <Option key={customer.id} value={customer.id}>
                  <div className="d-flex flex-column">
                    {customer.id}-{customer.business_name}
                    <small>{customer.customer_billing_details.address}{customer.customer_billing_details.address_line_1 ? `, ${customer.customer_billing_details.address_line_1}` : ""}</small>
                  </div>
                </Option>
              ))
            : ""}
        </Select>
      </div>

      <div className="product-advance-filter-field">
        <label>Filter by Payment Type</label>
        <Select
          dropdownClassName="po-report-select-dropdown"
          showArrow
          showSearch
          optionFilterProp="children"
          mode="multiple"
          maxTagCount={"responsive"}
          value={values?.payment_type || []}
          allowClear
          name="payment_type"
          placeholder={"Select Payment Type"}
          onChange={(value) => {
            setFieldValue("payment_type", value);
          }}
        >
          {Object.keys(PAYMENT_TYPE).map((type) => (
            <Select.Option key={type} value={PAYMENT_TYPE[type].value}>
              {PAYMENT_TYPE[type].type2}
            </Select.Option>
          ))}
        </Select>
      </div>
      <div className="product-advance-filter-field">
        <label>Filter by Payment Category</label>
        <Select
          dropdownClassName="po-report-select-dropdown"
          showArrow
          showSearch
          optionFilterProp="children"
          value={values.payment_categories || undefined}
          allowClear
          name="payment_categories"
          placeholder={"Select Payment Category"}
          onChange={(value) => {
            setFieldValue("payment_categories", value);
          }}
        >
          {Object.keys(PAYMENT_CATEGORY_PAYMENT_RECEIVED).map((type) => (
            <Select.Option key={type} value={PAYMENT_CATEGORY_PAYMENT_RECEIVED[type].value}>
              {PAYMENT_CATEGORY_PAYMENT_RECEIVED[type].type}
            </Select.Option>
          ))}
        </Select>
      </div>
      <div className="product-advance-filter-field">
        <label>Filter by Payment Range</label>
        <Select
          dropdownClassName="payment-range-dropdown po-report-select-dropdown"
          dropdownRender={() => (
            <div style={{ padding: 12 }}>
              <label style={{ color: "black" }}>Payment Range</label>
              <Row gutter={8} align="middle">
                <Col lg={11}>
                  <Input
                    placeholder="0"
                    value={minPayment}
                    onFocus={(e) => {
                      e.target.select();
                    }}
                    prefix={currencySymbol}
                    style={{ width: "100%" }}
                    onChange={(e) => {
                      const numericValue = handleNumericInput(e.target.value);
                      setMinPayment(numericValue);
                      setFieldValue("min_payment", numericValue);
                    }}
                  />
                </Col>
                <Col lg={2} style={{ textAlign: "center" }}>
                  <span style={{ color: "black" }}>To</span>
                </Col>
                <Col lg={11}>
                  <Input
                    placeholder="Any"
                    value={maxPayment}
                    prefix={currencySymbol}
                    style={{ width: "100%" }}
                    onChange={(e) => {
                      const numericValue = handleNumericInput(e.target.value);
                      setMaxPayment(numericValue);
                      setFieldValue("max_payment", numericValue);
                    }}
                  />
                </Col>
              </Row>
            </div>
          )}
          value={displayValue(minPayment, maxPayment)}
          placeholder="0 - Any"
        />
      </div>

      <div className="product-advance-filter-field product-advance-filter-full">
        <label>Filter by Date Range</label>
        <RangePicker
          style={{ width: "100%" }}
          format={dateFormat}
          placeholder={[dateFormat, dateFormat]}
          allowClear={false}
          ranges={{
            Today: [moment(getDateTimeInZone(timeZone)?.split("T")[0]), moment(getDateTimeInZone(timeZone)?.split("T")[0])],
            Yesterday: [
              moment(getDateTimeInZone(timeZone)?.split("T")[0])?.subtract(1, "day"),
              moment(getDateTimeInZone(timeZone)?.split("T")[0])?.subtract(1, "day"),
            ],
            "Last 7 Days": [moment(getDateTimeInZone(timeZone)?.split("T")[0])?.subtract(7, "days"), moment(getDateTimeInZone(timeZone)?.split("T")[0])],
            "Last 14 Days": [
              moment(getDateTimeInZone(timeZone)?.split("T")[0])?.subtract(14, "days"),
              moment(getDateTimeInZone(timeZone)?.split("T")[0]),
            ],
            "Last 30 Days": [
              moment(getDateTimeInZone(timeZone)?.split("T")[0])?.subtract(30, "days"),
              moment(getDateTimeInZone(timeZone)?.split("T")[0]),
            ],
          }}
          value={[
            values.start_date ? moment(values.start_date) : moment(getTimeByTimeZone(timeZone, startDate)),
            values.end_date ? moment(values.end_date) : moment(getTimeByTimeZone(timeZone, endDate)),
          ]}
          onChange={(value, dateString) => {
            setFieldValue("start_date", dateString[0]);
            setFieldValue("end_date", dateString[1]);
          }}
        />
      </div>
      <div className="product-advance-filter-field">
        <label>Filter by Reference Number</label>
        <Input
          className="form-control"
          value={values.reference_no}
          placeholder="Enter Reference number"
          onChange={({ target: { value } }) => {
            setFieldValue("reference_no", value);
          }}
        />
      </div>
      <div className="product-advance-filter-field">
        <label>Filter by Sale Order</label>
        {values.sale_order_no == "" ? (
          <Select
            dropdownClassName="po-report-select-dropdown"
            showSearch
            filterOption={false}
            onSearch={(value) => {
              getSaleListings({ sale_order_no: value });
            }}
            showArrow
            placeholder="Search Sale Order"
          >
            {!!saleOrders && !!saleOrders?.map
              ? saleOrders?.map((row) => (
                  <Option key={row.no} value={row.no} label={row.no}>
                    <div
                      className="d-flex align-items-center flex-grow-1 border rounded m-3 p-3"
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        setFieldValue("sale_order_no", row.no);
                      }}
                    >
                      <div className="d-flex flex-column ml-3 mt-2 mb-2">
                        <span className="font-weight-bold text-dark text-hover-primary">{row.no}</span>
                      </div>
                    </div>
                  </Option>
                ))
              : null}
          </Select>
        ) : (
          <div className="rounded border p-1 col-md-12 w-0">
            <div className="d-flex align-items-center flex-grow-1">
              <div className="d-flex flex-column ml-3 mt-2 mb-2">
                <span className="font-weight-bold text-dark">{values.sale_order_no}</span>
              </div>
              <a className="btn btn-icon btn-light btn-hover-danger btn-sm ml-auto" onClick={() => setFieldValue("sale_order_no", "")}>
                <span className="svg-icon svg-icon-md svg-icon-danger">
                  <SVG src={toAbsoluteUrl("/media/svg/icons/General/Trash.svg")} />
                </span>
              </a>
            </div>
          </div>
        )}
      </div>
      <div className="product-advance-filter-field">
        <label>Filter by Cheque Date</label>
        <DatePicker
          style={{ width: "100%" }}
          format={dateFormat}
          value={values?.cheque_date ? moment(values?.cheque_date) : null}
          showToday={false}
          onChange={(date, value) => {
            setFieldValue("cheque_date", value);
          }}
        />
      </div>
      <div className="product-advance-filter-field">
        <label>Filter by Cheque No.</label>
        <Input
          placeholder="Search by cheque no."
          value={values.cheque_no}
          onFocus={(e) => {
            e.target.select();
          }}
          onChange={(e) => {
            setFieldValue("cheque_no", e.target.value);
          }}
        />
      </div>
      <div className="product-advance-filter-field">
        <label>Filter by Users</label>
        <Select
          dropdownClassName="po-report-select-dropdown"
          mode="multiple"
          maxTagCount={"responsive"}
          filterOption={true}
          showSearch
          optionFilterProp="children"
          allowClear
          value={values?.user_ids || []}
          showArrow
          placeholder="Select by users"
          onChange={(value) => {
            setFieldValue("user_ids", value);
          }}
        >
          {(users || []).map((user) => (
            <Option key={user.id} value={user.id}>
              {user.first_name} {user.last_name}
            </Option>
          ))}
        </Select>
      </div>
      <div className="product-advance-filter-field">
        <label>Filter by Channel</label>
        <Select
          showArrow
          value={values?.channel_ids || []}
          showSearch
          optionFilterProp="children"
          name="channel_ids"
          placeholder={"Select Channel"}
          dropdownClassName="po-report-select-dropdown"
          mode="multiple"
          maxTagCount={"responsive"}
          onChange={(status) => {
            setFieldValue("channel_ids", status);
          }}
          onSearch={(search_key) => {
            getChannels({ search_key });
          }}
        >
          {channelOptions.map((channel) => (
            <Option key={channel.id} value={channel.id}>
              {channel.name}
            </Option>
          ))}
        </Select>
      </div>
    </div>
  );

  return (
    <Formik
      innerRef={formRef}
      initialValues={getFormInitialValues()}
      onSubmit={(values) => {
        applyFilter(values);
      }}
    >
      {({ values, handleSubmit, handleBlur, setFieldValue, resetForm, setValues }) => (
        <div className="stocks-search-row">
          <div className="stocks-search-field">
            <i className="fas fa-search stocks-search-icon" aria-hidden="true" />
            <DebounceInput
              type="text"
              className="form-control"
              debounceTimeout={DELAY}
              name="search_key"
              autoComplete="off"
              placeholder="Search by Business name"
              onBlur={handleBlur}
              value={values?.search_key}
              onChange={(e) => {
                setFieldValue("search_key", e.target.value.trimLeft());
                handleSubmit();
              }}
            />
            {values?.search_key && (
              <button
                type="button"
                className="stocks-search-clear"
                aria-label="Clear search"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  setFieldValue("search_key", "");
                  applyFilter({ ...values, search_key: "" });
                }}
              >
                <CloseOutlined />
              </button>
            )}
          </div>
          <div className="stocks-advanced-filter">
            <AdvancedFilterPopover
              placement="bottomLeft"
              zIndex={999}
              title={"Advance Filters"}
              // Applied filters only: the saved flag (set on Apply, cleared on Clear) and any API default date window.
              // Draft selections still open in the popover don't count.
              showFilters={Boolean(
                startDate ||
                endDate ||
                JSON.parse(localStorageFilter())?.show_advance_filters
              )}
              overlayClassName="stocks-advanced-filter-popover po-report-filter-popover"
              visible={popoverVisible}
              onVisibleChange={setPopoverVisible}
              contentWidth={520}
              onApply={() => {
                applyFilter(values);
                setPopoverVisible(false);
                setShowAppliedFilters?.(false);
                let payment_receive_filter = JSON.parse(localStorage.getItem("payment_receive_filter"));
                localStorage.setItem(
                  "payment_receive_filter",
                  JSON.stringify({ ...payment_receive_filter, show_advance_filters: true })
                );
              }}
              onClear={() => {
                const cleared = { ...initialValues };
                resetForm({ values: cleared });
                setValues(cleared);
                applyFilter(cleared);
                setPopoverVisible(false);
                setMinPayment(0);
                setMaxPayment(null);
                setShowAppliedFilters?.(true);
                localStorage.setItem(
                  "payment_receive_filter",
                  JSON.stringify({
                    advance_filters: cleared,
                    show_advance_filters: false,
                    show_default_start_date: true,
                  })
                );
              }}
              content={advanceFilterContent(values || {}, setFieldValue)}
              trigger="click"
            />
          </div>
        </div>
      )}
    </Formik>
  );
}